import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getEnvironments, ENVIRONMENT_STORAGE_KEY, type Environment } from '../../constants';

interface IEnvironmentContext {
  environment: Environment;
  setEnvironment: (environment: Environment) => void;
  isProd: boolean;
  canSwitch: boolean;
}

const EnvironmentContext = createContext<IEnvironmentContext | undefined>(undefined);

const isEnvironment = (value: unknown): value is Environment =>
  typeof value === 'string' && (getEnvironments() as string[]).includes(value);

const fallback = (): Environment => getEnvironments()[0];

const readStoredEnvironment = (): Environment => {
  try {
    const stored = globalThis.localStorage?.getItem(ENVIRONMENT_STORAGE_KEY);
    return isEnvironment(stored) ? stored : fallback();
  } catch {
    return fallback();
  }
};

export const EnvironmentProvider = ({ children }: { children: React.ReactNode }) => {
  const [environment, setEnvironmentState] = useState<Environment>(readStoredEnvironment);

  const setEnvironment = useCallback((next: Environment) => {
    setEnvironmentState(next);
    try {
      globalThis.localStorage?.setItem(ENVIRONMENT_STORAGE_KEY, next);
    } catch {
      // storage unavailable, keep in memory only
    }
  }, []);

  const value = useMemo(
    () => ({
      environment,
      setEnvironment,
      isProd: environment === 'prod',
      canSwitch: getEnvironments().length > 1,
    }),
    [environment, setEnvironment],
  );

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
};

export const useEnvironment = (): IEnvironmentContext => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used inside EnvironmentProvider');
  }
  return context;
};
